import { useApiClient } from './api-client.js';
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';

/**
 * Build a typed TanStack Query hook set for a CRUD resource.
 *
 * Conventions:
 * - Detail query key: `[queryKey, id]`
 * - List query key: `[queryKey, 'list', params ?? {}]`
 * - Create invalidates the list query.
 * - Update invalidates both the detail and list queries.
 * - Delete invalidates every query under `[queryKey]`.
 *
 * `useOne` unwraps the `{ success, data }` envelope and returns the entity
 * as the query's `data`. Mutations return the raw envelope so callers can read
 * the returned entity via `result.data.id`.
 */
function createEntityApi({ resource, queryKey }) {
    function buildListQueryString(params) {
        if (!params)
            return '';
        const entries = Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== '');
        if (entries.length === 0)
            return '';
        const search = new URLSearchParams();
        for (const [key, value] of entries) {
            search.set(key, String(value));
        }
        return `?${search.toString()}`;
    }
    return {
        useOne(id) {
            const apiClient = useApiClient();
            return useQuery({
                queryKey: [queryKey, id],
                queryFn: async () => {
                    const res = await apiClient.get(`/${resource}/${id}`);
                    return res.data;
                },
                enabled: !!id,
            });
        },
        useMany(params) {
            const apiClient = useApiClient();
            return useQuery({
                queryKey: [queryKey, 'list', params ?? {}],
                queryFn: () => apiClient.get(`/${resource}${buildListQueryString(params)}`),
            });
        },
        useCreate() {
            const apiClient = useApiClient();
            const queryClient = useQueryClient();
            return useMutation({
                mutationFn: (data) => apiClient.post(`/${resource}`, data),
                onSuccess: () => {
                    queryClient.invalidateQueries({ queryKey: [queryKey, 'list'] });
                },
            });
        },
        useUpdate() {
            const apiClient = useApiClient();
            const queryClient = useQueryClient();
            return useMutation({
                mutationFn: ({ id, data }) => apiClient.patch(`/${resource}/${id}`, data),
                onSuccess: (_result, { id }) => {
                    queryClient.invalidateQueries({ queryKey: [queryKey, id] });
                    queryClient.invalidateQueries({ queryKey: [queryKey, 'list'] });
                },
            });
        },
        useDelete() {
            const apiClient = useApiClient();
            const queryClient = useQueryClient();
            return useMutation({
                mutationFn: async (id) => {
                    await apiClient.delete(`/${resource}/${id}`);
                },
                onSuccess: () => {
                    queryClient.invalidateQueries({ queryKey: [queryKey] });
                },
            });
        },
    };
}

export { createEntityApi };
//# sourceMappingURL=createEntityApi.js.map
